import React, { useCallback } from "react";
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Switch,
  Textarea,
  useTheme,
  Button,
  InputGroup,
  InputRightElement,
  FormHelperText,
} from "@chakra-ui/react";
import Select, { components } from "react-select";
import { RiEyeLine, RiEyeOffLine } from "react-icons/ri";
import { useState } from "react";
import SelectAsync from "react-select/async";

import "react-datepicker/dist/react-datepicker.css";
import { FaChevronDown } from "react-icons/fa";

type LoadOptionsType = (
  inputValue: string,
  callback: (options: { label: string; value: string }[]) => void
) => void;

interface CustomInputProps {
  type?:
  | "editor"
  | "password"
  | "number"
  | "text"
  | "file"
  | "switch"
  | "textarea"
  | "select"
  | "date"
  | "phone"
  | "file-drag"
  | "select-async";
  label?: string;
  placeholder?: string;
  isRequired?: boolean;
  error?: any;
  maxDate?: any;
  minDate?: any;
  disabledDates?: any;
  showPassword?: boolean;
  name: string;
  isClear?: boolean;
  onChange?: (value: any) => void;
  value?: any;
  w?: string;
  options?: any[];
  isSearchable?: boolean;
  isMulti?: boolean;
  getOptionLabel?: any;
  getOptionValue?: any;
  rows?: number;
  disabled?: boolean;
  showError?: boolean;
  style?: any;
  phone?: string;
  loadOptions?: LoadOptionsType;
  // Callback for file drop
  onFileDrop?: (files: FileList) => void;
  loading?: any;
  onInputChange?: any;
  props?: any;
  rest?: any;
  minH?: any;
  floating?: boolean;
  isBoxSelect?: boolean;
  isReadOnly?: boolean;
}

const CustomInput = ({
  type,
  label,
  placeholder,
  error,
  name,
  value,
  onChange,
  isRequired,
  isClear,
  options,
  isSearchable,
  isMulti,
  getOptionLabel,
  getOptionValue,
  disabled,
  rows,
  style,
  showError,
  maxDate,
  minDate,
  disabledDates,
  phone,
  loadOptions,
  loading,
  onFileDrop,
  onInputChange,
  props,
  showPassword,
  // Added onFileDrop prop
  rest,
  minH,
  floating,
  isBoxSelect,
  isReadOnly,
}: CustomInputProps) => {
  const theme = useTheme();
  const [showPasswords, setShowPasswords] = useState(showPassword);
  const [isFocused, setIsFocused] = useState(false);
  const customStyle = {
    control: (provided: any, state: any) => ({
      ...provided,
      width: rest?.width || "100%",
      maxHeight: "10rem",
      overflowY: "auto",
      "min-height": minH ? minH : "38px",
      [`@media screen and (max-width: ${theme.breakpoints.md})`]: {
        ...rest,
      },
      // [`@media screen and (max-width: ${theme.breakpoints.md})`]: {
      //   width: "100%",
      // },
      border: state.isFocused ? "2px solid #38b2ac" : "1px solid #e2e8f0",
      "&:hover": {
        // border: "1px solid #b0c5d6",
        cursor: "pointer",
      },
      color: "rgba(5, 14, 41, 0.4)",
      fontSize: "16px",

      fontWeight: "400",
      height: "37px",
      paddingLeft: "0.5rem",

      boxShadow: "none",
      backgroundColor: "transparent",
      outline: "none",
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999, // Ensure the dropdown appears above everything
      position: "absolute",
    }),
    menuPortal: (provided: any) => ({ ...provided, zIndex: 9999 }),
    option: (provided: any, state: any) => ({
      ...provided,
      fontSize: "16px",
      outline: "none",
      "&:hover": {
        backgroundColor: "#B2F5EA",
        cursor: "pointer",
      },
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: "2px 0px",
      outline: "none",
    }),
    multiValue: (styles: any) => {
      // const color = chroma(data.color);
      return {
        ...styles,
        backgroundColor: "#4FD1C5",
      };
    },
    clearIndicator: (provided: any) => ({
      ...provided,
      position: "sticky",
      top: 0,
      right: 10,
      cursor: "pointer",
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      position: "relative",
    }),
    multiValueLabel: (styles: any) => ({
      ...styles,
      color: "white",
    }),
    multiValueRemove: (styles: any) => ({
      ...styles,
      color: "white",
      ":hover": {
        backgroundColor: "teal.400",
        color: "white",
      },
    }),
    MenuOptionGroup: (styles: any) => ({
      ...styles,
      zIndex: 9999,
    }),
  };

  const DropdownIndicator = (props: any) => {
    return (
      <components.DropdownIndicator {...props}>
        <FaChevronDown />
      </components.DropdownIndicator>
    );
  };

  const handleTogglePassword = () => {
    setShowPasswords(!showPasswords);
  };

  const handleFileDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const files = event.dataTransfer.files;
      console.log(files);
      if (onFileDrop) {
        onFileDrop(files);
      }
    },
    [onFileDrop]
  );

  const renderInputComponent = () => {
    switch (type) {
      case "password":
        return (
          <InputGroup>
            <Input
              type={showPasswords ? "text" : "password"}
              pr="4.5rem"
              position="relative"
              placeholder={placeholder}
              value={value}
              onChange={onChange}
              name={name}
              isRequired={isRequired}
              disabled={disabled}
              fontSize="sm"
              {...rest}
            />
            <InputRightElement
              cursor="pointer"
              onClick={() => {
                if (handleTogglePassword) {
                  handleTogglePassword();
                }
              }}
            >
              {showPasswords ? (
                <RiEyeOffLine size={18} />
              ) : (
                <RiEyeLine size={18} />
              )}
            </InputRightElement>
          </InputGroup>
        );
      case "number":
        return (
          <Input
            type="number"
            style={style}
            value={value}
            onChange={onChange}
            name={name}
            placeholder={placeholder}
            disabled={disabled}
            fontSize="sm"
            {...props}
            {...rest}
          />
        );
      case "text":
        return (
          <Input
            type="text"
            placeholder={placeholder}
            style={style}
            value={value}
            onChange={onChange}
            name={name}
            disabled={disabled}
            fontSize="sm"
            isReadOnly={isReadOnly}
            {...rest}
          />
        );
      case "date":
        return (
          <Input
            type="date"
            placeholder={placeholder}
            style={style}
            value={value}
            onChange={onChange}
            name={name}
            disabled={disabled}
            fontSize="sm"
            isReadOnly={isReadOnly}
            max={maxDate || undefined} // ⬅️ Restrict selection after today
            {...rest}
          />
        );
      case "textarea":
        return (
          <Textarea
            style={style}
            rows={rows || 3}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            name={name}
            disabled={disabled}
            fontSize="sm"
            {...rest}
          />
        );
      case "switch":
        return (
          <Switch
            style={style}
            colorScheme="teal"
            _focus={{ outline: "none" }}
            name={name}
            isChecked={value}
            {...rest}
            isDisabled={disabled}
            onChange={onChange}
          />
        );
      case "select-async":
        return (
          <FormControl id="select" variant={floating ? "floating" : undefined}>
            <SelectAsync
              name="fetchUser"
              value={value}
              onChange={onChange}
              isClearable={isClear}
              getOptionLabel={getOptionLabel}
              getOptionValue={getOptionValue}
              loadOptions={loadOptions}
              defaultOptions={options}
              isLoading={loading}
              fontSize="sm"
              placeholder={
                floating ? undefined : placeholder ? placeholder : "Search..."
              }
              isDisabled={disabled}
              styles={customStyle}
              theme={(theme) => ({
                ...theme,
                colors: {
                  ...theme.colors,
                  primary: "#38B2AC",
                  primary25: "#B2F5EA",
                  primary50: "#B2F5EA",
                },
              })}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              menuPosition={isBoxSelect ? "fixed" : undefined}
              {...rest}
            />
            {floating && (
              <FormHelperText
                style={{
                  position: "absolute",
                  zIndex: 2,
                  backgroundColor: "#f8f9fa",
                  left: "20px",
                  fontSize: "14px",
                  top: value || isFocused ? "-18px" : "30%",
                  transition: "top 0.3s",
                  transform: value || isFocused ? "none" : "translateY(-50%)",
                  fontWeight: 500,
                }}
              >
                {label}
              </FormHelperText>
            )}
          </FormControl>
        );
      case "select":
        return (
          <FormControl style={style} id="select" variant={floating ? "floating" : undefined}>
            <Select
              options={options}
              placeholder={
                floating ? undefined : placeholder ? placeholder : "Search..."
              }
              value={value}
              onChange={(value: any) => {
                if (onChange) {
                  onChange(value);
                }
              }}
              isMulti={isMulti}
              theme={(theme) => ({
                ...theme,
                colors: {
                  ...theme.colors,
                  primary: "#38B2AC",
                  primary25: "#B2F5EA",
                  primary50: "#B2F5EA",
                },
              })}
              isSearchable={isSearchable}
              getOptionLabel={getOptionLabel}
              getOptionValue={getOptionValue}
              isDisabled={disabled}
              components={{
                IndicatorSeparator: null,
                DropdownIndicator,
              }}
              // menuPortalTarget={document.body}
              menuPosition={isBoxSelect ? "fixed" : undefined}
              styles={customStyle}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              {...rest}
            />
            {floating && (
              <FormHelperText
                style={{
                  position: "absolute",
                  zIndex: 0,
                  backgroundColor: "#f8f9fa",
                  left: "20px",
                  fontSize: "14px",
                  top: value || isFocused ? "-18px" : "30%",
                  transition: "top 0.3s",
                  transform: value || isFocused ? "none" : "translateY(-50%)",
                  fontWeight: 500,
                }}
              >
                {label}
              </FormHelperText>
            )}
          </FormControl>
        );
      case "file":
        return (
          <Input
            style={style}
            type="file"
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            name={name}
            disabled={disabled}
            fontSize="sm"
            {...rest}
            className="chooseFile"
          />
        );
      case "file-drag":
        return (
          <div
            style={{
              border: "2px dashed #ddd",
              borderRadius: "8px",
              padding: "1rem",
              textAlign: "center",
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleFileDrop}
          >
            <p>Drag & drop files here or click to browse</p>
            <input
              type="file"
              name={name}
              multiple={isMulti}
              onChange={onChange}
              style={{ display: "none" }}
              id={`multiple-file-upload-with-draggable-${name}`}
            />
            <Button
              colorScheme="blue"
              onClick={() =>
                (
                  document.getElementById(
                    `multiple-file-upload-with-draggable-${name}`
                  ) as unknown as HTMLInputElement
                )?.click()
              }
            >
              Browse
            </Button>
          </div>
        );
      default:
        return (
          <Input
            style={style}
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            name={name}
            disabled={disabled}
            fontSize="sm"
            isReadOnly={isReadOnly}
            {...rest}
          />
        );
    }
  };

  return floating ? (
    <FormControl
      id={name}
      isInvalid={!!error && showError}
      variant={"floating"}
      {...style}
    >
      {renderInputComponent()}
      {type !== "select" && type !== "select-async" && (
        <FormLabel       {...style}
          fontSize="sm" style={{ ...style }} textTransform="capitalize">
          {label} {isRequired && <span style={{ color: "red" }}>*</span>}
        </FormLabel>
      )}
      {showError && error && <FormErrorMessage>{error}</FormErrorMessage>}
    </FormControl>
  ) : (
    <FormControl       {...style}
      id={name} isInvalid={!!error && showError}>
      <FormLabel fontSize="sm" textTransform="capitalize">
        {label} {isRequired && <span style={{ color: "red" }}>*</span>}
      </FormLabel>
      {renderInputComponent()}
      {showError && error && <FormErrorMessage>{error}</FormErrorMessage>}
    </FormControl>
  );
};

export default CustomInput;
